
import React from 'react';
import NavBar from '@/components/layout/NavBar';
import Footer from '@/components/layout/Footer';
import FadeInSection from '@/components/ui/FadeInSection';
import GlassCard from '@/components/ui/GlassCard';
import { Button } from '@/components/ui/button';
import { 
  Shield, 
  Key, 
  Lock, 
  UserCheck, 
  Users, 
  Globe, 
  Cloud, 
  Check, 
  Fingerprint, 
  BrainCircuit, 
  Scale, 
  BarChart3, 
  Clock,
  Database
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/components/i18n/language-provider';

const Business = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen flex flex-col">
      <NavBar />
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-6">
          {/* Hero Section */}
          <FadeInSection>
            <div className="text-center mb-16">
              <span className="inline-block rounded-full px-3 py-1 text-sm bg-brand-purple/10 text-brand-purple font-medium mb-4">
                {t("business.title")}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-6">{t("business.hero.title")}</h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-4xl mx-auto">
                {t("business.hero.description")}
              </p>
            </div>
          </FadeInSection>

          {/* Introduction */}
          <FadeInSection delay={100}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center mb-20">
              <div>
                <h2 className="text-3xl font-bold mb-6">{t("business.intro.title")}</h2>
                <p className="text-lg text-muted-foreground mb-6">
                  {t("business.intro.description")}
                </p>
                <Button 
                  className="bg-brand-purple hover:bg-brand-purple/90 text-white"
                  onClick={() => window.open("https://meetings-na2.hubspot.com/gilmar/demo-keystle-website", "_blank")}
                >
                  {t("business.getStarted")}
                </Button>
              </div>
              <div className="rounded-lg overflow-hidden shadow-xl">
                <img 
                  src="/lovable-uploads/6ef6ca7c-288c-44d7-9c04-588e8c108833.png" 
                  alt={t("business.intro.imageAlt")}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                  width="600" 
                  height="400"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>
          </FadeInSection>

          {/* Key Features Section */}
          <FadeInSection delay={200}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t("business.features.title")}</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  {t("business.features.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Unified Dashboard */}
                <GlassCard className="p-6">
                  <div className="text-brand-purple mb-4">
                    <BarChart3 className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("business.features.dashboard.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("business.features.dashboard.description")}
                  </p>
                </GlassCard>

                {/* Multi-Tenant Management */}
                <GlassCard className="p-6">
                  <div className="text-brand-purple mb-4">
                    <Users className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("business.features.multiTenant.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("business.features.multiTenant.description")}
                  </p>
                </GlassCard>

                {/* Passwordless Authentication */}
                <GlassCard className="p-6">
                  <div className="text-brand-purple mb-4">
                    <Key className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("business.features.passwordless.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("business.features.passwordless.description")}
                  </p>
                </GlassCard>

                {/* SSO */}
                <GlassCard className="p-6">
                  <div className="text-brand-purple mb-4">
                    <UserCheck className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("business.features.sso.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("business.features.sso.description")}
                  </p>
                </GlassCard>

                {/* MFA with OTP and Email */}
                <GlassCard className="p-6">
                  <div className="text-brand-purple mb-4">
                    <Shield className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("business.features.mfa.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("business.features.mfa.description")}
                  </p>
                </GlassCard>

                {/* API-First */}
                <GlassCard className="p-6">
                  <div className="text-brand-purple mb-4">
                    <Cloud className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold">{t("business.features.api.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("business.features.api.description")}
                  </p>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* Add-ons Section */}
          <FadeInSection delay={300}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t("business.addons.title")}</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  {t("business.addons.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Behavioral Analytics */}
                <GlassCard className="p-6">
                  <div className="text-brand-purple mb-4">
                    <BrainCircuit className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("business.addons.analytics.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("business.addons.analytics.description")}
                  </p>
                </GlassCard>

                {/* Fraud Prevention */}
                <GlassCard className="p-6">
                  <div className="text-brand-purple mb-4">
                    <Fingerprint className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("business.addons.fraud.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("business.addons.fraud.description")}
                  </p>
                </GlassCard>

                {/* Adaptive Authentication */}
                <GlassCard className="p-6">
                  <div className="text-brand-purple mb-4">
                    <Lock className="h-10 w-10" />
                  </div>
                  <h3 className="text-xl font-semibold mb-3">{t("business.addons.adaptive.title")}</h3>
                  <p className="text-muted-foreground mb-4">
                    {t("business.addons.adaptive.description")}
                  </p>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* Performance Section */}
          <FadeInSection delay={400}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t("business.performance.title")}</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  {t("business.performance.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <GlassCard className="p-8">
                  <div className="flex items-center mb-6">
                    <Clock className="h-8 w-8 text-brand-purple mr-4" />
                    <h3 className="text-2xl font-semibold">{t("business.performance.architecture.title")}</h3>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-purple mr-3 mt-0.5 flex-shrink-0" />
                      <span>{t("business.performance.architecture.item1")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-purple mr-3 mt-0.5 flex-shrink-0" />
                      <span>{t("business.performance.architecture.item2")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-purple mr-3 mt-0.5 flex-shrink-0" />
                      <span>{t("business.performance.architecture.item3")}</span>
                    </li>
                  </ul>
                </GlassCard>

                <GlassCard className="p-8">
                  <div className="flex items-center mb-6">
                    <Database className="h-8 w-8 text-brand-purple mr-4" />
                    <h3 className="text-2xl font-semibold">{t("business.performance.compliance.title")}</h3>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-purple mr-3 mt-0.5 flex-shrink-0" />
                      <span>{t("business.performance.compliance.item1")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-purple mr-3 mt-0.5 flex-shrink-0" />
                      <span>{t("business.performance.compliance.item2")}</span>
                    </li>
                    <li className="flex items-start">
                      <Check className="h-5 w-5 text-brand-purple mr-3 mt-0.5 flex-shrink-0" />
                      <span>{t("business.performance.compliance.item3")}</span>
                    </li>
                  </ul>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* Use Cases */}
          <FadeInSection delay={500}>
            <div className="mb-20">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold mb-4">{t("business.useCases.title")}</h2>
                <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                  {t("business.useCases.description")}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* SaaS Platforms */}
                <GlassCard className="p-6">
                  <h3 className="text-xl font-semibold mb-3 flex items-center">
                    <Cloud className="h-6 w-6 text-brand-purple mr-2" />
                    {t("business.useCases.saas.title")}
                  </h3>
                  <p className="text-muted-foreground">
                    {t("business.useCases.saas.description")}
                  </p>
                </GlassCard>

                {/* Enterprise Identity */}
                <GlassCard className="p-6">
                  <h3 className="text-xl font-semibold mb-3 flex items-center">
                    <Users className="h-6 w-6 text-brand-purple mr-2" />
                    {t("business.useCases.enterprise.title")}
                  </h3>
                  <p className="text-muted-foreground">
                    {t("business.useCases.enterprise.description")}
                  </p>
                </GlassCard>

                {/* Financial Services */}
                <GlassCard className="p-6">
                  <h3 className="text-xl font-semibold mb-3 flex items-center">
                    <Shield className="h-6 w-6 text-brand-purple mr-2" />
                    {t("business.useCases.financial.title")}
                  </h3>
                  <p className="text-muted-foreground">
                    {t("business.useCases.financial.description")}
                  </p>
                </GlassCard>

                {/* Healthcare */}
                <GlassCard className="p-6">
                  <h3 className="text-xl font-semibold mb-3 flex items-center">
                    <Lock className="h-6 w-6 text-brand-purple mr-2" />
                    {t("business.useCases.healthcare.title")}
                  </h3>
                  <p className="text-muted-foreground">
                    {t("business.useCases.healthcare.description")}
                  </p>
                </GlassCard>

                {/* B2B Marketplaces */}
                <GlassCard className="p-6">
                  <h3 className="text-xl font-semibold mb-3 flex items-center">
                    <Globe className="h-6 w-6 text-brand-purple mr-2" />
                    {t("business.useCases.b2b.title")}
                  </h3>
                  <p className="text-muted-foreground">
                    {t("business.useCases.b2b.description")}
                  </p>
                </GlassCard>
                
                {/* Cross-Organization */}
                <GlassCard className="p-6">
                  <h3 className="text-xl font-semibold mb-3 flex items-center">
                    <Scale className="h-6 w-6 text-brand-purple mr-2" />
                    {t("business.useCases.crossOrg.title")}
                  </h3>
                  <p className="text-muted-foreground">
                    {t("business.useCases.crossOrg.description")}
                  </p>
                </GlassCard>
              </div>
            </div>
          </FadeInSection>

          {/* CTA Section */}
          <FadeInSection delay={600}>
            <div className="text-center py-16 px-6 bg-gradient-to-r from-brand-purple/10 to-transparent rounded-2xl">
              <h2 className="text-3xl font-bold mb-4">{t('business.cta.title')}</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Stay connected with confidence and focus on what truly matters growing your business.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button 
                  className="bg-brand-purple hover:bg-brand-purple/90 text-white"
                  size="lg"
                  onClick={() => window.open("https://meetings-na2.hubspot.com/gilmar/demo-keystle-website", "_blank")}
                >
                  {t('business.getStarted')}
                </Button>
              </div>
            </div>
          </FadeInSection>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Business;
